
import { Component } from 'vue-property-decorator';
import uuid from 'uuid';
import * as echarts from 'echarts';
import moment from 'moment';
import LineChartFilterable from '@/components/reports-v3/components/filterables/LineChartFilterable.vue';
import { COL, sqlExtractYear, sqlExtractMonth, sqlExtractDay, sqlSum, getTableName } from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';

@Component({
	components: {
		LineChartFilterable,
	},
})
export default class SalesOrderTrendLineChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange'];

	public xAxisData: string[] = [];
	public periodTotalData: any[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get color() {
		return [GobiColor.COLOR1];
	}

	public get groupedBy() {
		const difference = this.selectedDateRange[1] - this.selectedDateRange[0];
		if (difference >= 365 * 3 * 24 * 60 * 60 * 1000) {
			return 'year';
		}

		if (difference >= 62 * 24 * 60 * 60 * 1000) {
			return 'month';
		}

		return 'day';
	}

	/**
	 * Key for data fetching - only changes when filters change
	 */
	private get _dataFetchKey(): string {
		return JSON.stringify([
			this.selectedDateRange,
			this.selectedAgents,
			this.selectedStockItems,
		]);
	}

	private _cachedData: {
		amounts: number[];
		labels: string[];
	} | null = null;
	private _cachedDataKey: string = '';

	public get expensiveHook() {
		const { selectedDateRange, selectedAgents, selectedStockItems } = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedStockItems,
		]);
	}

	public async expensiveCalc() {
		const jobId = (this.currentJobId = uuid.v4());
		this.$forceUpdate();
		this.generateLoadingText(0.2, 0.3);

		const currentFetchKey = this._dataFetchKey;
		const useCache = this._cachedData !== null && this._cachedDataKey === currentFetchKey;

		let amounts: number[];
		let labels: string[];

		if (useCache) {
			amounts = this._cachedData!.amounts;
			labels = this._cachedData!.labels;
		} else {
			this.generateLoadingText(0.3, 0.9);

			const queryHead = await this._ensureTablesLoaded(['salesOrder'] as any[]);
			this.checkInterrupted(jobId);

			const salesOrderTable = getTableName('salesOrder');
			const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
			const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
			const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
			const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
				&& this.selectedAgents.length !== allAgentLen
				? ` AND ${COL.AGENT} IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
				: '';
			const allStockItemsLen = (gdbx.allStockItems && gdbx.allStockItems.length) || 0;
			const stockItemFilter = this.selectedStockItems && this.selectedStockItems.length > 0
				&& this.selectedStockItems.length !== allStockItemsLen
				? ` AND ${COL.ITEMCODE} IN (${this.selectedStockItems.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
				: '';

			const rawData = await this._loadSalesOrderDataByPeriod(
				queryHead, salesOrderTable, `${dateFilter}${agentFilter}${stockItemFilter}`,
			);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.95);

			labels = this._buildPeriodLabels();
			amounts = this._alignToPeriodArray(rawData, (i) => i.amount);

			this._cachedData = { amounts, labels };
			this._cachedDataKey = currentFetchKey;
		}

		this.generateLoadingText(1);

		this.xAxisData = labels;

		const sales = {
			name: 'Sales Order',
			data: amounts,
			type: 'line',
			areaStyle: {
				color: GobiColor.COLOR1,
				opacity: 1,
			},
			smooth: true,
		};

		this.series = [sales];

		this.saveHistory('series', 'xAxisData', 'periodTotalData');
	}

	private _getPeriodSqlParts(): { select: string; groupBy: string; orderBy: string } {
		const p = this.groupedBy;
		if (p === 'year') {
			return {
				select: sqlExtractYear(COL.DOCDATE),
				groupBy: 'year',
				orderBy: 'year',
			};
		} else if (p === 'month') {
			return {
				select: `${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)}`,
				groupBy: 'year, month',
				orderBy: 'year, month',
			};
		} else {
			return {
				select: `${sqlExtractYear(COL.DOCDATE)}, ${sqlExtractMonth(COL.DOCDATE)}, ${sqlExtractDay(COL.DOCDATE)}`,
				groupBy: 'year, month, day',
				orderBy: 'year, month, day',
			};
		}
	}

	private _buildPeriodLabels(): string[] {
		const p = this.groupedBy;
		const start = moment(this.selectedDateRange[0]);
		const end = moment(this.selectedDateRange[1]);
		const labels: string[] = [];

		if (p === 'year') {
			const current = start.clone().startOf('year');
			while (current.isSameOrBefore(end)) {
				labels.push(current.format('YYYY'));
				current.add(1, 'year');
			}
		} else if (p === 'month') {
			const current = start.clone().startOf('month');
			while (current.isSameOrBefore(end)) {
				labels.push(current.format('MMM YY'));
				current.add(1, 'month');
			}
		} else {
			const current = start.clone().startOf('day');
			while (current.isSameOrBefore(end)) {
				labels.push(current.format('DD MMM'));
				current.add(1, 'day');
			}
		}

		return labels;
	}

	private async _loadSalesOrderDataByPeriod(
		queryHead: any, tableName: string, whereClause: string,
	): Promise<Array<{
		year: number; month: number; day: number;
		amount: number;
	}>> {
		const { select, groupBy, orderBy } = this._getPeriodSqlParts();

		const results = await queryHead.rawQuery(`
			SELECT
				${select},
				${sqlSum(COL.AMOUNT, 'amount')}
			FROM ${tableName}
			WHERE ${whereClause}
			GROUP BY ${groupBy}
			ORDER BY ${orderBy}
		`);

		return results.map((r: any) => ({
			year: Number(r.year),
			month: Number(r.month) || 0,
			day: Number(r.day) || 0,
			amount: Number(r.amount) || 0,
		}));
	}

	private _alignToPeriodArray<T extends { year: number; month: number; day: number }>(
		data: T[],
		getValue: (item: T) => number,
	): number[] {
		const p = this.groupedBy;
		const start = moment(this.selectedDateRange[0]);
		const end = moment(this.selectedDateRange[1]);

		const indexMap = new Map<string, number>();
		const current = start.clone();
		let pos = 0;

		if (p === 'year') {
			current.startOf('year');
			while (current.isSameOrBefore(end)) {
				indexMap.set(`${current.year()}`, pos++);
				current.add(1, 'year');
			}
		} else if (p === 'month') {
			current.startOf('month');
			while (current.isSameOrBefore(end)) {
				indexMap.set(`${current.year()}-${current.month() + 1}`, pos++);
				current.add(1, 'month');
			}
		} else {
			current.startOf('day');
			while (current.isSameOrBefore(end)) {
				indexMap.set(`${current.year()}-${current.month() + 1}-${current.date()}`, pos++);
				current.add(1, 'day');
			}
		}

		const result = Array(pos).fill(0);

		for (const item of data) {
			let key: string;
			if (p === 'year') {
				key = `${item.year}`;
			} else if (p === 'month') {
				key = `${item.year}-${item.month || 1}`;
			} else {
				key = `${item.year}-${item.month || 1}-${item.day || 1}`;
			}
			const idx = indexMap.get(key);
			if (idx !== undefined) {
				result[idx] = getValue(item);
			}
		}

		return result;
	}
}
